/// <reference types="vite/client" />
import ReactGA from "react-ga4"; // https://www.npmjs.com/package/react-ga4
import { 
  EVENT_NAMES, 
  EVENT_CATEGORIES,
  ENVIRONMENTS,
  ANALYTICS_LOG_PREFIX,
} from "./AnalyticsConstants";
import { getEnvironment } from "./EnvironmentUtils";
import { initSLAMonitoring } from "../SLAMonitoring/SLAMonitoringClient";
import { logger } from "./Logger";

// Module-level variable to store the current game ID
let currentGameId: string | null = null;

// Declare gtag types for TypeScript
declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

// Updates the page title to include "(development)" suffix if in dev environment
const updatePageTitleForEnvironment = (): void => {
  if (typeof document === 'undefined') return;
  
  const environment = getEnvironment();
  const currentTitle = document.title;
  
  // Only add suffix if in development and not already present
  if (environment === ENVIRONMENTS.DEVELOPMENT && !currentTitle.includes(`(dev)`)) {
    document.title = `${currentTitle} (dev)`;
    logger.debug(`${ANALYTICS_LOG_PREFIX} Updated page title to include environment: ${document.title}`);
  }
};

// Initializes page-view reporting via Google Analytics and CloudWatch monitoring.
export const initializeAnalytics = (gameId?: string): void => {
  const measurementId = import.meta.env.VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID;
  
  // Store the game ID if provided
  if (gameId) {
    currentGameId = gameId;
    logger.debug(`${ANALYTICS_LOG_PREFIX} Initialized with game_id: ${gameId}`);
    
    // Automatically initialize CloudWatch monitoring with the same game ID
    initSLAMonitoring({
      pageName: gameId,
    });
  }
  
  if (measurementId) {
    const environment = getEnvironment();

    // Passed as config params (not just user properties) so automatic events like page_view,
    // session_start and first_visit also carry them; otherwise `customEvent:environment` is
    // `(not set)` for those and dev/preview traffic can't be filtered out of view counts.
    ReactGA.initialize(measurementId, {
      gtagOptions: {
        environment: environment,
        game_id: gameId || 'unknown'
      }
    });

    ReactGA.gtag('set', 'user_properties', {
      environment: environment,
      game_id: gameId || 'unknown'
    });
    
    logger.debug(`${ANALYTICS_LOG_PREFIX} Set user properties - environment: ${environment}, game_id: ${gameId || 'unknown'}`);
  } else {
    logger.warn(`${ANALYTICS_LOG_PREFIX} Skipping Google Analytics initialization, since no measurement ID could be found.`);
  }
  
  // Update page title to include environment indicator
  updatePageTitleForEnvironment();
};

// Helper function to get the current game ID
export const getCurrentGameId = (): string | null => {
  return currentGameId;
};

interface ReportEventParams {
  action: string;
  category: string;
  label?: string;
  value?: number;
}

// Reports an event to Google Analytics.
export const reportEvent = ({ action, category, label, value }: ReportEventParams): void => {
  try {
    const gameId = getCurrentGameId();
    const environment = getEnvironment();
    
    // Build event parameters
    const eventParams: Record<string, any> = {
      event_category: category,
      event_label: label,
      value: value,
      game_id: gameId || 'unknown',
      environment: environment
    };
    
    // Debug log to track what events are being sent
    logger.debug(`${ANALYTICS_LOG_PREFIX} Reporting event: ${action}`, eventParams);
    
    // Use GA4 event method signature with custom parameters
    ReactGA.event(action, eventParams);
  } catch (error) {
    logger.warn(`${ANALYTICS_LOG_PREFIX} Error reporting event:`, error);
  }
};

// Error tracking (stretch goals)
export const reportError = (errorMessage: string, errorType: string = 'javascript'): void => {
  try {
    const gameId = getCurrentGameId();
    const environment = getEnvironment();
    
    ReactGA.event(EVENT_NAMES.ERROR_OCCURRED, {
      event_category: EVENT_CATEGORIES.ERRORS,
      error_message: errorMessage.substring(0, 100), // Limit length
      error_type: errorType,
      game_id: gameId || 'unknown',
      environment: environment
    });
  } catch (error) {
    logger.warn(`${ANALYTICS_LOG_PREFIX} Error reporting error:`, error);
  }
};

// Wrapper for console.error to auto-track errors
const originalConsoleError = console.error;
console.error = (...args: any[]) => {
  const errorMessage = args.map(arg => 
    typeof arg === 'string' ? arg : JSON.stringify(arg)
  ).join(' ');
  
  reportError(errorMessage, 'console_error');
  originalConsoleError.apply(console, args);
};